import React, { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useSelector, useDispatch } from 'react-redux'
import { RootState } from '../store'
import { setRooms, setLoading } from '../store/roomsSlice'
import roomsService from '../services/roomsService'
import RoomCard from './RoomCard'
import OnboardingConciergeModal from './agent/OnboardingConciergeModal'
import { useTranslation } from '../hooks/use-i18n'
import { HIDE_IN_APP_CHESS } from '../constants/chessProduct'

interface RoomsSectionProps {
  onOpenCreateRoom?: () => void
  /** @deprecated CHESS_HIDDEN — in-app chess create disabled; use chessarenas.com */
  onOpenCreateChessArena?: () => void
  onWorldEnrolled?: (gameType?: 'football' | 'formula1', extras?: { tournamentIds: string[]; countryCode: string; city: string }) => void
}

const RoomsSection: React.FC<RoomsSectionProps> = ({ onOpenCreateRoom, onWorldEnrolled }) => {
  const { rooms } = useSelector((state: RootState) => state.rooms)
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const [showConciergeModal, setShowConciergeModal] = useState(false)

  const { data: roomsData, isLoading, isFetching } = useQuery({
    queryKey: ['user-rooms'],
    queryFn: () => roomsService.getUserRooms(),
    staleTime: 60 * 1000,
    retry: 1,
  })

  useEffect(() => {
    if (roomsData !== undefined) dispatch(setRooms(roomsData || []))
  }, [roomsData, dispatch])

  useEffect(() => {
    dispatch(setLoading(isLoading))
  }, [isLoading, dispatch])

  // CHESS_HIDDEN: filter chess rooms out of My Rooms (play on chessarenas.com)
  const visibleRooms = useMemo(
    () => (HIDE_IN_APP_CHESS ? rooms.filter((r) => r.gameType !== 'chess') : rooms),
    [rooms],
  )

  const showRoomsSpinner = isLoading && visibleRooms.length === 0 && rooms.length === 0

  return (
    <section className="py-3 sm:py-6 lg:py-8">
      <div className="flex items-start justify-between gap-3 mb-4 sm:mb-6">
        <div className="min-w-0">
          <h2 className="font-display text-lg sm:text-display-2 font-bold text-foreground flex items-center gap-2">
            {t('dashboard.yourRooms')}
            {isFetching && !showRoomsSpinner && (
              <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            )}
          </h2>
          <p className="text-xs sm:text-sm text-muted-foreground mt-1">{t('dashboard.yourRoomsHint')}</p>
        </div>
        {onOpenCreateRoom && (
          <button
            type="button"
            onClick={() => onOpenCreateRoom()}
            className="shrink-0 inline-flex items-center gap-1 px-3.5 py-2 rounded-full bg-sky-300 text-slate-900 text-sm font-extrabold hover:bg-sky-200 transition-colors"
          >
            + {t('dashboard.newRoomCta')}
          </button>
        )}
      </div>

      {showRoomsSpinner ? (
        <div className="flex justify-center items-center py-8 sm:py-12">
          <div className="animate-spin rounded-full h-8 w-8 sm:h-12 sm:w-12 border-b-2 border-primary" />
        </div>
      ) : visibleRooms.length === 0 ? (
        <div className="text-center py-6 sm:py-8 lg:py-16">
          <div className="glass-card rounded-xl sm:rounded-2xl p-4 sm:p-8 lg:p-12 max-w-md mx-auto">
            <div className="text-4xl sm:text-6xl lg:text-8xl mb-3 sm:mb-4 lg:mb-6 animate-pulse">🏠</div>
            <h3 className="font-display text-lg sm:text-display-2 font-semibold mb-2 sm:mb-3 lg:mb-4 text-foreground">{t('dashboard.noRoomsYet')}</h3>
            <p className="text-small sm:text-body-lg text-muted-foreground mb-4 sm:mb-6 lg:mb-8 leading-relaxed">
              {t('dashboard.noRoomsYetDesc')}
            </p>
            <button
              onClick={() => onOpenCreateRoom?.()}
              className="w-full px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg sm:rounded-xl bg-green-800 text-white font-semibold text-small sm:text-nav hover:bg-green-700 transition-all"
            >
              {t('dashboard.createFirstRoom')}
            </button>
            <button
              type="button"
              onClick={() => setShowConciergeModal(true)}
              className="w-full mt-2 px-4 py-2.5 rounded-xl border border-primary/40 text-foreground font-medium text-small hover:bg-primary/5 transition-all"
            >
              ✨ {t('agent.askConcierge')}
            </button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-4 lg:gap-8">
          {visibleRooms.map((room, index) => (
            <div key={room._id} className="animate-fade-in" style={{ animationDelay: `${index * 0.1}s` }}>
              <RoomCard room={room} />
            </div>
          ))}
        </div>
      )}
      {showConciergeModal && (
        <OnboardingConciergeModal
          onClose={() => setShowConciergeModal(false)}
          onEnrolled={(gameType, extras) => {
            setShowConciergeModal(false)
            onWorldEnrolled?.(gameType, extras)
          }}
          onOpenCreateRoom={() => onOpenCreateRoom?.()}
        />
      )}
    </section>
  )
}

export default RoomsSection
